import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import BlueBanner from './shared/BlueBanner';
import Loader from './shared/Loader';
import NotRenderedInExperienceEditor from '../NotRenderedInExperienceEditor';
import TrimCardGridUI from './TrimCardGridUI';
import getInventoryStatusWarningMessage from './utils/getInventoryStatusWarningMessage';
import getLeadFormParamsByAppName from './utils/getLeadFormParamsByAppName';
import getLimitedInventoryTrimsGtm from './utils/getLimitedInventoryTrimsGtm';
import getSelectedModelSitecoreId from './utils/getSelectedModelSitecoreId';
import getSubPagePath from './utils/getSubPagePath';
import useFilteredCardsByVariant from './hooks/useFilteredCardsByVariant';
import useIncentiveOffers from '../PaymentCalculator/ViewOffers/useIncentiveOffers';
import useLegacyInventoryStatus from './hooks/useLegacyInventoryStatus';
import useModelData from './hooks/useModelData';
import usePageTitle from '../../utils/hooks/usePageTitle';
import useGetLinkSearchParams, { DEFAULT_PROVIDER_SERVICE, FEATURE_ORIGIN_INV } from './hooks/useGetLinkSearchParams';
import { ACURA_SITE_NAME, CONTACT_DEALER_URL } from '../../utils/constants';
import { TrimCardVariant } from './TrimCard';
import { getComponentFromRoute } from '../../utils/sitecorePlaceholders';
import { getModelComparePath } from '../../utils/urls';
import { primaryCalculatorId } from '../../core/reducers/financialCalculators';
import { resetVehicleListFilters } from '../../core/reducers/vehicleListFilters';
import { selectBodyTypesForGtm } from '../../core/reducers/vehicles';
import { selectDealer } from '../../core/reducers/dealer';
import { setBackStepUrl, setIsTrimCardGridActive, setTrimCardGridNextStepUrl as setNextStepUrl, } from '../../core/reducers/ui';
import { useAppName, useLanguage, usePageState, useRoute, useSharedApps } from '../../utils/sitecoreContext';
import { useAppSelector } from '../../core/utils';
const INVENTORY_VARIANT = 'variant2';
const TrimCardGrid = ({ fields, rendering, params, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6;
    // parse Sitecore data
    const models = (_f = (_e = (_d = (_c = (_b = (_a = fields === null || fields === void 0 ? void 0 : fields.data) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.catalog) === null || _c === void 0 ? void 0 : _c.fields) === null || _d === void 0 ? void 0 : _d.models) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.items;
    const nextStepUrl = ((_k = (_j = (_h = (_g = fields === null || fields === void 0 ? void 0 : fields.data) === null || _g === void 0 ? void 0 : _g.value) === null || _h === void 0 ? void 0 : _h.nextStepBaseUrl) === null || _j === void 0 ? void 0 : _j.item) === null || _k === void 0 ? void 0 : _k.value) || {};
    const backStepUrl = ((_p = (_o = (_m = (_l = fields === null || fields === void 0 ? void 0 : fields.data) === null || _l === void 0 ? void 0 : _l.value) === null || _m === void 0 ? void 0 : _m.backStepBaseUrl) === null || _o === void 0 ? void 0 : _o.item) === null || _p === void 0 ? void 0 : _p.value) || {};
    const title = (_s = (_r = (_q = fields === null || fields === void 0 ? void 0 : fields.data) === null || _q === void 0 ? void 0 : _q.value) === null || _r === void 0 ? void 0 : _r.title) === null || _s === void 0 ? void 0 : _s.value;
    const showNameBadge = (_v = (_u = (_t = fields === null || fields === void 0 ? void 0 : fields.data) === null || _t === void 0 ? void 0 : _t.value) === null || _u === void 0 ? void 0 : _u.showNameBadge) === null || _v === void 0 ? void 0 : _v.value;
    const showAdditionalFeatures = !!((_y = (_x = (_w = fields === null || fields === void 0 ? void 0 : fields.data) === null || _w === void 0 ? void 0 : _w.value) === null || _x === void 0 ? void 0 : _x.showAdditionalFeatures) === null || _y === void 0 ? void 0 : _y.value);
    const inventoryStatusPosition = (_2 = (_1 = (_0 = (_z = fields === null || fields === void 0 ? void 0 : fields.data) === null || _z === void 0 ? void 0 : _z.value) === null || _0 === void 0 ? void 0 : _0.inventoryStatusPosition) === null || _1 === void 0 ? void 0 : _1.value) === null || _2 === void 0 ? void 0 : _2.toLowerCase();
    const gtmComponentName = rendering === null || rendering === void 0 ? void 0 : rendering.componentName;
    const isInventoryVariant = (params === null || params === void 0 ? void 0 : params.variant) === INVENTORY_VARIANT;
    const pageState = usePageState();
    const appName = useAppName();
    const { t } = useTranslation();
    const route = useRoute();
    const dispatch = useDispatch();
    const language = useLanguage();
    // values from redux store
    const gtmBodyStyle = useSelector(selectBodyTypesForGtm);
    const { dealerName, dealerCode } = useAppSelector(selectDealer);
    const currentVehicle = useAppSelector(state => state.currentVehicle);
    // By default, fetch trims for selected model key and selected model year;
    // For inventory variant, fetch trims for selected model key and ALL model years.
    const selectedModelKey = currentVehicle.modelKey;
    const selectedModelYear = isInventoryVariant ? '' : currentVehicle.modelYear;
    const selectedModelSitecoreId = getSelectedModelSitecoreId(selectedModelKey, models);
    const { trims, defaultTrim, nameBadge } = useModelData(selectedModelSitecoreId, selectedModelYear);
    const pageTitle = usePageTitle((_3 = trims === null || trims === void 0 ? void 0 : trims[0]) === null || _3 === void 0 ? void 0 : _3.modelName, isInventoryVariant);
    // default trim for GTM
    const defaultTrimGtmData = (trims === null || trims === void 0 ? void 0 : trims.find(trim => trim.trimKey === defaultTrim)) || {
        trimKey: '',
        trimName: '',
        detIdentifier: '',
    };
    const { trimName: gtmTrimName, detIdentifier } = defaultTrimGtmData;
    const hasFourColumn = !!getComponentFromRoute('SingleColumnLayout', route);
    const canShowInventoryCta = ((_6 = (_5 = (_4 = fields === null || fields === void 0 ? void 0 : fields.data) === null || _4 === void 0 ? void 0 : _4.value) === null || _5 === void 0 ? void 0 : _5.showInventoryCta) === null || _6 === void 0 ? void 0 : _6.value) === '1';
    // update redux store on mount and unmount
    useEffect(() => {
        dispatch(setIsTrimCardGridActive(true));
        dispatch(setBackStepUrl(backStepUrl));
        dispatch(setNextStepUrl(nextStepUrl));
        return () => {
            dispatch(resetVehicleListFilters());
            dispatch(setNextStepUrl({ href: '', text: '' }));
        };
    }, []);
    // Compare Trims link
    const specificationsPagePath = !isInventoryVariant &&
        trims.length > 1 &&
        getSubPagePath(models, selectedModelKey || '', selectedModelYear || '', 'specifications');
    const compareLink = specificationsPagePath
        ? {
            label: t('Pages.Bap.compareTrimsButton'),
            url: getModelComparePath(specificationsPagePath, detIdentifier, language),
        }
        : undefined;
    // Filtered cards, for default variant or inventory variant
    const { trimCards, isFetching, isUninitialized, error } = useFilteredCardsByVariant(isInventoryVariant, trims.length > 0, selectedModelKey, selectedModelYear);
    // set inventoryStatusWarningMessage
    const { data: trimsInventoryStatus } = useLegacyInventoryStatus(trimCards, !isInventoryVariant);
    const inventoryStatusWarningMessage = getInventoryStatusWarningMessage(trimCards, trimsInventoryStatus);
    // availability disclaimer contact dealer link
    const contactDealerBaseUrl = useSharedApps(CONTACT_DEALER_URL);
    const searchParams = useGetLinkSearchParams({
        dealerCode,
        providerService: DEFAULT_PROVIDER_SERVICE,
        generatePayload: getLeadFormParamsByAppName(appName, FEATURE_ORIGIN_INV),
    }, false);
    // get trim names for availability disclaimer gtm
    const gtmLimitedTrims = getLimitedInventoryTrimsGtm(trimCards, trimsInventoryStatus);
    // get all offers for the all trims
    useIncentiveOffers(primaryCalculatorId, true, isInventoryVariant);
    /**
     * TODO:
     * - global inventory status errors (needs design)
     */
    if (pageState === 'edit') {
        return _jsx(NotRenderedInExperienceEditor, { componentName: rendering.componentName });
    }
    if (!fields)
        return null;
    if ((isInventoryVariant && isFetching) || (isInventoryVariant && isUninitialized))
        return _jsx(Loader, {});
    if (error) {
        return _jsx(BlueBanner, { title: t('Shared.Common.unableToLoadError'), subtitle: t('Shared.Common.unableToLoadText') });
    }
    // Inventory availablity of default trim for compare gtm
    const defaultTrimStatus = trimsInventoryStatus === null || trimsInventoryStatus === void 0 ? void 0 : trimsInventoryStatus.find(trim => trim.trimKey === defaultTrim);
    const defaultTrimAvailability = defaultTrimStatus === null || defaultTrimStatus === void 0 ? void 0 : defaultTrimStatus.inventoryStatus;
    let canShowChevronInSubtitle = false;
    switch (appName) {
        case ACURA_SITE_NAME:
            canShowChevronInSubtitle = true;
            break;
        default:
            break;
    }
    return (_jsx(TrimCardGridUI, { backStepUrl: { text: backStepUrl === null || backStepUrl === void 0 ? void 0 : backStepUrl.text, href: backStepUrl === null || backStepUrl === void 0 ? void 0 : backStepUrl.href }, canShowChevronInSubtitle: canShowChevronInSubtitle, compareLink: compareLink, contactDealerLink: isInventoryVariant ? `${contactDealerBaseUrl}?${searchParams}` : undefined, dealerName: dealerName, defaultTrimAvailability: defaultTrimAvailability, defaultTrimGtmData: defaultTrimGtmData, gtmBodyStyle: gtmBodyStyle, gtmComponentName: gtmComponentName, gtmLimitedTrims: gtmLimitedTrims, gtmTrimName: gtmTrimName, hasFourColumnLayout: hasFourColumn, inventoryStatusPosition: inventoryStatusPosition, inventoryStatusWarningMessage: inventoryStatusWarningMessage, nameBadge: showNameBadge ? nameBadge : undefined, nextStepUrl: nextStepUrl, pageTitle: pageTitle, showAdditionalFeatures: showAdditionalFeatures, showInventoryCta: canShowInventoryCta, title: title, trimCardVariant: isInventoryVariant ? TrimCardVariant.Inventory : TrimCardVariant.Default, trims: trimCards, trimsInventoryStatus: trimsInventoryStatus }));
};
export default TrimCardGrid;
